import * as yup from 'yup';
import { cloneDeep, sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { type Company } from '~/common/types/company/company';
import { type PhoneNumberData } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.types';
import { type RouterOutputs } from '@/config/trpc';

import { AddressFunction, type CaseRole, Language } from '~/common/enums';
import { languageMap } from '~/common/constants';

import { getInitialPhoneNumbers } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.utils';

import { usePhoneNumbersSchema } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.hooks';

import {
  hasAtMostOneDefaultIban,
  hasDefaultIban,
  hasNoDuplicateIbans,
} from '@/shared/utils/ibanListRules';
import { containsHtml } from '@/shared/utils/url';
import htmlToPlainText from '@/shared/utils/htmlText';

export type CompanyFormValues = {
  addresses: ValueTypes['address'][];
  companyId: string | null;
  companyTags: string[];
  defaultCaseRole: ValueTypes['caseRole'] | null;
  directDebitEnabled: boolean;
  directDebitHomeownerAssociationIds: string[];
  emailAddresses: ValueTypes['emailAddress'][];
  exactOnlinePaymentCondition: ValueTypes['exactOnlinePaymentCondition'] | null;
  externalReference: string | null;
  ibans: ValueTypes['iban'][];
  isActive: boolean;
  isMemberOfContractorNetwork: boolean;
  language: ValueTypes['language'];
  name: string | null;
  note: string | null;
  paymentPeriod: number | null;
  phoneNumbers: ValueTypes['phoneNumberMulti'][];
  pushCrmChanges: boolean;
  secondaryLanguages: ValueTypes['secondaryLanguage'][];
  showContractorNameOnSalesInvoice: boolean;
  vatNumber: string | null;
};

export type CompanyInput = Partial<Company | RouterOutputs['company']['byId']>;

type SchemaAddress = Omit<Partial<ValueTypes['address']>, 'function'> & {
  function?: string | null;
};

type ValueTypes = {
  address: {
    addressLine: string | null;
    city: string | null;
    country: string | null;
    description: string | null;
    function: ValueTypes['companyAddress']['function'];
    postalCode: string | null;
  };
  caseRole: NonNullable<Company['defaultCaseRole']>;
  companyAddress: Company['companyAddresses'][number];
  emailAddress: {
    email: string | null;
    emailContext: number[];
  };
  exactOnlinePaymentCondition: NonNullable<Company['exactOnlinePaymentCondition']>;
  iban: Company['ibans'][number] & { tempKey: string };
  language: NonNullable<Company['language']>;
  phoneNumberMulti: PhoneNumberData;
  secondaryLanguage: NonNullable<Company['secondaryLanguages']>[number];
};

const DEFAULT_COMPANY_VALUES: CompanyFormValues = {
  addresses: [],
  companyId: null,
  companyTags: [],
  defaultCaseRole: null,
  directDebitEnabled: false,
  directDebitHomeownerAssociationIds: [],
  emailAddresses: [],
  exactOnlinePaymentCondition: null,
  externalReference: null,
  ibans: [],
  isActive: true,
  isMemberOfContractorNetwork: false,
  language: Language.English,
  name: null,
  note: null,
  paymentPeriod: null,
  phoneNumbers: [],
  pushCrmChanges: false,
  secondaryLanguages: [],
  showContractorNameOnSalesInvoice: false,
  vatNumber: null,
};

const DEFAULT_EMAIL_ADDRESS_VALUE: ValueTypes['emailAddress'] = { email: null, emailContext: [0] };

const languageValues = Object.values(Language);

const getInitialAddresses = (company: CompanyInput) =>
  company.companyAddresses?.map((address) => ({
    addressLine: address.address?.addressLine ?? null,
    city: address.address?.city ?? null,
    country: address.address?.country ?? null,
    description: address.description ?? null,
    function: address.function,
    postalCode: address.address?.postalCode ?? null,
  }));

const getInitialIBANs = (company: CompanyInput) => {
  if (!company.ibans) return undefined;

  const ibans: ValueTypes['iban'][] = company.ibans.map((iban) => ({
    accountNumber: iban.accountNumber,
    bic: iban.bic,
    isDefault: iban.isDefault,
    tempKey: uuidv4(),
  }));

  return sortBy(ibans, (iban) => !iban.isDefault);
};

const getInitialTags = (company: CompanyInput) => company.companyTags?.map((tag) => tag.tagId);

const isNonEmpty = (value: string | null | undefined) => !!value && !!value.trim();

const isAddressPartiallyFilled = (address: SchemaAddress) =>
  [
    address?.addressLine,
    address?.city,
    address?.country,
    address?.postalCode,
    address?.function,
  ].some(isNonEmpty);

type Options = {
  isContractor?: boolean;
  minified?: boolean;
};

const useAddEditCompanyFormConfig = (initialCompany?: CompanyInput, options?: Options) => {
  const { isContractor = false, minified = false } = options ?? {};

  const { i18n, t } = useTranslation('errors');

  const multiPhoneNumbersSchema = usePhoneNumbersSchema();

  const getDefaultValues = useCallback(() => {
    const localeLang = languageMap[i18n.language];
    const values = cloneDeep(DEFAULT_COMPANY_VALUES);

    if (!minified) values.emailAddresses = [cloneDeep(DEFAULT_EMAIL_ADDRESS_VALUE)];
    if (isContractor) values.isMemberOfContractorNetwork = true;
    if (localeLang) values.language = localeLang;

    return values;
  }, [i18n.language, isContractor, minified]);

  const initialValues = useMemo((): CompanyFormValues => {
    const defaultValues = getDefaultValues();

    if (!initialCompany) return defaultValues;

    return {
      addresses: getInitialAddresses(initialCompany) ?? defaultValues.addresses,
      companyId: initialCompany.id ?? defaultValues.companyId,
      companyTags: getInitialTags(initialCompany) ?? defaultValues.companyTags,
      defaultCaseRole: initialCompany.defaultCaseRole ?? defaultValues.defaultCaseRole,
      directDebitEnabled: !!initialCompany.directDebitHomeownerAssociationIds?.length,
      directDebitHomeownerAssociationIds:
        initialCompany.directDebitHomeownerAssociationIds ??
        defaultValues.directDebitHomeownerAssociationIds,
      emailAddresses:
        (initialCompany.emailAddresses as ValueTypes['emailAddress'][] | undefined) ??
        defaultValues.emailAddresses,
      exactOnlinePaymentCondition:
        initialCompany.exactOnlinePaymentCondition ?? defaultValues.exactOnlinePaymentCondition,
      externalReference: initialCompany.externalReference ?? defaultValues.externalReference,
      ibans: getInitialIBANs(initialCompany) ?? defaultValues.ibans,
      isActive: initialCompany.isActive ?? defaultValues.isActive,
      isMemberOfContractorNetwork:
        initialCompany.isContractor ?? defaultValues.isMemberOfContractorNetwork,
      language: initialCompany.language ?? defaultValues.language,
      name: initialCompany.displayName ?? defaultValues.name,
      note: htmlToPlainText(initialCompany.note) ?? defaultValues.note,
      paymentPeriod: initialCompany.paymentPeriod ?? defaultValues.paymentPeriod,
      phoneNumbers: getInitialPhoneNumbers(initialCompany) ?? defaultValues.phoneNumbers,
      pushCrmChanges: false,
      secondaryLanguages: initialCompany.secondaryLanguages ?? defaultValues.secondaryLanguages,
      showContractorNameOnSalesInvoice: false,
      vatNumber: initialCompany.vatNumber ?? defaultValues.vatNumber,
    };
  }, [getDefaultValues, initialCompany]);

  const addressSchema = useMemo(() => {
    const requiredWhenSiblingNonEmptySchema = yup
      .string()
      .nullable()
      .optional()
      .test(
        'required-when-sibling-non-empty',
        t('fieldIsRequired'),
        (value, ctx) => !isAddressPartiallyFilled(ctx.parent) || isNonEmpty(value),
      );

    return yup.object().shape({
      addressLine: requiredWhenSiblingNonEmptySchema,
      city: requiredWhenSiblingNonEmptySchema,
      country: requiredWhenSiblingNonEmptySchema,
      description: yup.string().nullable().optional(),
      function: requiredWhenSiblingNonEmptySchema,
      postalCode: requiredWhenSiblingNonEmptySchema,
    });
  }, [t]);

  const addressesSchema = useMemo(
    () =>
      yup.array(addressSchema).test('hasSingleHQ', t('hasHQ'), (addresses) => {
        if (!addresses?.length) return true;

        const hqCount = addresses.filter(
          (address) => address.function === AddressFunction.HQ && isAddressPartiallyFilled(address),
        ).length;

        return hqCount <= 1;
      }),
    [addressSchema, t],
  );

  const baseSchema = useMemo(
    () =>
      yup.object({
        defaultCaseRole: yup.mixed<CaseRole>().nullable(),
        language: yup.string().nullable().oneOf(languageValues).required(t('fieldIsRequired')),
        name: yup.string().nullable().required(t('fieldIsRequired')),
      }),
    [t],
  );

  const emailAddressesSchema = useMemo(() => {
    const itemSchema = yup.object({
      email: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .email(t('invalidEmail')),
      emailContext: yup.array(yup.number()),
    });

    return yup.array(itemSchema);
  }, [t]);

  const ibansSchema = useMemo(() => {
    const itemSchema = yup.object({
      accountNumber: yup.string().nullable().required(t('fieldIsRequired')).accountNumber(),
      bic: yup.string().nullable().required(t('fieldIsRequired')).bic(),
      isDefault: yup.boolean(),
    });

    return yup
      .array(itemSchema)
      .test('hasDefault', t('defaultValueIsRequired'), hasDefaultIban)
      .test('hasOnlyOneDefault', t('onlyOneDefaultValueIsAllowed'), hasAtMostOneDefaultIban)
      .test('hasNoDuplicateIbans', t('ibanAlreadyAdded'), hasNoDuplicateIbans);
  }, [t]);

  const isEditing = !!initialCompany?.id;

  const expandedSchema = useMemo(() => {
    const extendedSchema = baseSchema.shape({
      // Addresses are only editable on create; on edit they live on the Addresses tab.
      // Validating them here blocks save with no visible errors.
      ...(isEditing ? {} : { addresses: addressesSchema }),
      directDebitHomeownerAssociationIds: yup.array().of(yup.string().uuid()),
      emailAddresses: emailAddressesSchema,
      ibans: ibansSchema,
      isMemberOfContractorNetwork: yup.boolean(),
      name: yup.string().nullable().required(t('fieldIsRequired')),
      note: yup
        .string()
        .nullable()
        .test('mustNotContainHtml', t('mustNotContainHtml'), (value) => !containsHtml(value)),
      paymentPeriod: yup.number().nullable().min(0, t('valueMustBePositive')),
      vatNumber: yup.string().nullable(),
    });

    return multiPhoneNumbersSchema.extend(extendedSchema);
  }, [
    addressesSchema,
    baseSchema,
    emailAddressesSchema,
    ibansSchema,
    isEditing,
    multiPhoneNumbersSchema,
    t,
  ]);

  return {
    baseValidationSchema: baseSchema,
    expandedValidationSchema: expandedSchema,
    initialValues,
  };
};

export default useAddEditCompanyFormConfig;
